// ─────────────────────────────────────────────────────────────────────────────
// Task 4.5 — traduce `NominationRawExportRowDto` (Task 4.3/4.4) a las columnas del Excel crudo de
// nominaciones (spec §4.1, calcado de `08 - Ganadores.xlsx`).
//
// Task 16 — REVIERTE el criterio "libro técnico, no texto de UI" con el que nació este mapper: los
// encabezados ahora se resuelven con `i18n` (namespace `recognition`, `admin.winners.
// nominationsExport.*`) y siguen el idioma de quien exporta, igual que el resto de la pantalla.
//
// Task 9 — la hoja pasó de una cabecera plana de una fila (`XLSX.utils.json_to_sheet`) al mecanismo
// ACTUAL: `construirHojaExcelNominaciones` arma un `ExcelSheetSpec` de array-of-arrays con 2 filas de
// encabezado (nombre de Fundamental fusionado + nombre de Hábito) vía `buildMatrizHabitosHeader`/
// `desplazarMerge` (`exportExcelMultiSheet.ts`, `exceljs`), la misma técnica que ya usa "Todos los
// reconocimientos" de Brillamos Juntos. Ya no hay columnas "dinámicas por objeto": las columnas de
// hábito son POSICIONALES, en el mismo orden que la matriz `fundamentalesConHabitos` que recibe.
//
// Fix crítico (revisión de rama completa, 2026-08-31) — esa matriz debe cubrir la UNIÓN de todos los
// hábitos citados en las nominaciones exportadas, incluso si el catálogo cambió entre una y otra (un
// Hábito desactivado DESPUÉS de que una nominación lo citara no puede desaparecer del Excel que el
// comité usa para decidir premios). El caller (`AdminWinners.tsx`) es quien arma esa unión, con
// `resolverFundamentalesConHabitosParaExport` (`../fundamentalesHabitosMatrix.ts`) en vez de la
// variante activo-solamente que sigue usando `ShineTab.tsx`; esta función solo consume el resultado.
// ─────────────────────────────────────────────────────────────────────────────
import type { TFunction } from 'i18next';
import type { NominationRawExportRowDto } from '@/api/recognition';
import {
  buildMatrizHabitosHeader, desplazarMerge, type ExcelCellValue, type ExcelSheetSpec,
} from '@/utils/exportExcelMultiSheet';
import { celdasDeHabitos, type FundamentalConHabitosResueltos } from '../fundamentalesHabitosMatrix';

/**
 * Marca de campo cumplido en el Excel. NO se usa "Sí"/"No": una X (o vacío) se entiende en
 * cualquiera de los 7 idiomas sin traducir, y evita que alguien filtre por la palabra "Sí" en un
 * idioma equivocado.
 */
const MARCADO = 'X';
const NO_MARCADO = '';

const NOMBRE_HOJA = 'Nominaciones';

/**
 * Task 9 (pedido del usuario, 2026-08-31) — arma la hoja completa del export de Ganadores con las
 * columnas de hábito agrupadas visualmente por Fundamental, igual técnica que "Todos los
 * reconocimientos" (Brillamos Juntos): 2 filas de encabezado + merge del nombre del Fundamental
 * sobre sus columnas de hábito. A diferencia de esa hoja, las columnas FIJAS siguen traducidas
 * (`admin.winners.nominationsExport.*`), no en inglés literal — este export es para el comité
 * multi-idioma de Ganadores, no calcado 1:1 de una plantilla fija.
 */
export function construirHojaExcelNominaciones(
  nominaciones: NominationRawExportRowDto[],
  fundamentalesConHabitos: FundamentalConHabitosResueltos[],
  t: TFunction,
): ExcelSheetSpec {
  const col = (key: string) => t(`admin.winners.nominationsExport.${key}`);

  const columnasFijasIzquierda = [
    col('nomineeName'), col('nomineeEmail'), col('nomineeEmployeeId'), col('nomineeOu'),
    col('nomineeBu'), col('nomineeSite'), col('nomineeDepartment'),
    col('onBehalfOfName'), col('onBehalfOfEmail'), col('onBehalfOfEmployeeId'), col('onBehalfOfOu'),
    col('onBehalfOfBu'), col('onBehalfOfSite'), col('onBehalfOfDepartment'),
    col('candidateName'), col('candidateEmail'), col('candidateEmployeeId'), col('candidateOu'),
    col('candidateBu'), col('candidateSite'), col('candidateDepartment'),
    col('date'), col('year'), col('month'),
  ];
  const columnasFijasDerecha = [
    col('comment'), col('streakBadge'), col('fromDayToDay'), col('status'), col('type'),
    col('audienceNominee'), col('audienceCandidate'), col('points'),
  ];

  const { headerRows: matrizHeader, merges } = buildMatrizHabitosHeader(fundamentalesConHabitos);
  const offset = columnasFijasIzquierda.length;

  const filaHeaderFundamentales: ExcelCellValue[] = [
    ...columnasFijasIzquierda.map(() => null), ...matrizHeader[0], ...columnasFijasDerecha.map(() => null),
  ];
  const filaHeaderColumnas: ExcelCellValue[] = [
    ...columnasFijasIzquierda, ...matrizHeader[1], ...columnasFijasDerecha,
  ];

  const filas = nominaciones.map((fila): ExcelCellValue[] => [
    fila.nomineeName, fila.nomineeEmail ?? '', fila.nomineeEmployeeId ?? '', fila.nomineeOu ?? '',
    fila.nomineeBu ?? '', fila.nomineeSite ?? '', fila.nomineeDepartment ?? '',
    fila.onBehalfOfName ?? '', fila.onBehalfOfEmail ?? '', fila.onBehalfOfEmployeeId ?? '',
    fila.onBehalfOfOu ?? '', fila.onBehalfOfBu ?? '', fila.onBehalfOfSite ?? '', fila.onBehalfOfDepartment ?? '',
    fila.candidateName, fila.candidateEmail ?? '', fila.candidateEmployeeId ?? '', fila.candidateOu ?? '',
    fila.candidateBu ?? '', fila.candidateSite ?? '', fila.candidateDepartment ?? '',
    fila.date.slice(0, 10), fila.anio, fila.mes,
    ...celdasDeHabitos(fila, fundamentalesConHabitos),
    fila.comment ?? '', fila.badgeDailyStreak ?? '', fila.fromDayToDayCampaign ? MARCADO : NO_MARCADO,
    fila.status, fila.tipo, fila.audienceNominee ?? '', fila.audienceCandidate ?? '', fila.puntos ?? '',
  ]);

  return {
    name: NOMBRE_HOJA,
    rows: [filaHeaderFundamentales, filaHeaderColumnas, ...filas],
    styleHints: { headerRows: [0, 1], merges: merges.map((rango) => desplazarMerge(rango, offset)) },
  };
}
